import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, Stack, Badge } from '@mui/material';
import { GetApp as GetAppIcon, AccountCircle as AccountCircleIcon, Security as SecurityIcon, Lightbulb as LightbulbIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useInstallPWA } from '../hooks/useInstallPWA';
import { useAuth } from '../contexts/AuthContext';
import { clueService } from '../services/clueService';
import { getUnseenCluesCount } from '../utils/seenCluesStorage';

export const Header: React.FC = () => {
  const { supportsPWA, isIOS, isInStandaloneMode, handleInstallClick } = useInstallPWA();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [unseenCluesCount, setUnseenCluesCount] = useState(0);
  const [activeClues, setActiveClues] = useState<string[]>([]);

  // Effect to subscribe to active clues
  useEffect(() => {
    if (!user) return;

    // Subscribe to active clues
    const unsubscribe = clueService.subscribeToActiveClues((clues) => {
      // Store the active clue IDs
      const clueIds = clues.map(clue => clue.id);
      setActiveClues(clueIds);

      // Calculate unseen clues count
      if (location.pathname !== '/clues') {
        const count = getUnseenCluesCount(user.uid, clueIds);
        setUnseenCluesCount(count);
      } else {
        // If we're on the clues page, count should be 0
        setUnseenCluesCount(0);
      }
    });

    return () => unsubscribe();
  }, [user, location.pathname]);

  // Effect to reset unseen count when on clues page
  useEffect(() => {
    if (location.pathname === '/clues') {
      setUnseenCluesCount(0);
    } else if (user && activeClues.length > 0) {
      // Recalculate count when navigating away from clues page
      const count = getUnseenCluesCount(user.uid, activeClues);
      setUnseenCluesCount(count);
    }
  }, [location.pathname, user, activeClues]);

  // Determine if we need to add safe area padding for iOS
  const needsSafeArea = isIOS && isInStandaloneMode;

  return (
    <AppBar
      position="static"
      className={needsSafeArea ? 'ios-pwa-header' : ''}
      sx={{
        background: 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)',
        // Add padding at the top for iOS notch and status bar when in standalone mode
        ...(needsSafeArea && {
          paddingTop: 'env(safe-area-inset-top)',
        })
      }}
    >
      <Toolbar>
        <SecurityIcon sx={{ mr: 1, color: '#00ff00' }} />
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            cursor: 'pointer',
            fontFamily: "'Share Tech Mono', monospace",
            letterSpacing: '0.1em',
            textShadow: '0 0 10px #00ff00',
          }}
          onClick={() => navigate('/')}
        >
          Cash Stash
        </Typography>
        <Stack direction="row" spacing={1}>
          {supportsPWA && !isInStandaloneMode && (
            <Button
              color="inherit"
              startIcon={<GetAppIcon />}
              onClick={handleInstallClick}
              sx={{
                borderColor: '#00ff00',
                '&:hover': {
                  backgroundColor: 'rgba(0, 255, 0, 0.1)',
                }
              }}
            >
              {isIOS ? 'Install Guide' : 'Install App'}
            </Button>
          )}
          {user && (
            <Button
              color="inherit"
              startIcon={
                <Badge
                  color="error"
                  badgeContent={unseenCluesCount > 0 ? unseenCluesCount : null}
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      right: -3,
                      top: 3,
                      border: '2px solid #1a1a1a',
                      padding: '0 4px',
                      fontFamily: "'Share Tech Mono', monospace",
                    },
                  }}
                >
                  <LightbulbIcon />
                </Badge>
              }
              onClick={() => {
                // Immediately set the count to 0 for better UX
                setUnseenCluesCount(0);
                navigate('/clues');
              }}
              sx={{
                borderColor: '#00ff00',
                '&:hover': {
                  backgroundColor: 'rgba(0, 255, 0, 0.1)',
                }
              }}
            >
              Clues
            </Button>
          )}
          <Button
            color="inherit"
            startIcon={<AccountCircleIcon />}
            onClick={() => navigate('/profile')}
            sx={{
              borderColor: '#00ff00',
              '&:hover': {
                backgroundColor: 'rgba(0, 255, 0, 0.1)',
              }
            }}
          >
            {user ? 'My Profile' : 'Sign In'}
          </Button>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};